import { Link, createFileRoute } from "@tanstack/react-router";
import {
  Archive,
  ArchiveRestore,
  ArrowLeft,
  Car,
  Loader2,
  Pencil,
  Ruler,
  UserRound,
} from "lucide-react";
import { type ReactNode, useState } from "react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  LicensePlate,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  VehicleImage,
  useToast,
} from "@rumbledirect/ui-v2";
import { MobilityCardsSection } from "@/charging/MobilityCardsSection";
import { useCurrentUser } from "@/currentUser/CurrentUserProvider";
import { hasPermission } from "@/currentUser/permissions";
import { RequirePermission } from "@/currentUser/RequirePermission";
import { DetailSection, FieldGrid } from "@/dossiers/DetailSection";
import { DOCUMENT_READ_PERMISSION } from "@/documents/documentPermissions";
import { DocumentsTab } from "@/documents/DocumentsTab";
import { FinesTab } from "@/fines/FinesTab";
import { useGetAdminVehiclesVehicleId } from "@/lib/api/generated/vehicle/vehicle";
import { NOTE_READ_PERMISSION } from "@/notes/notePermissions";
import { NotesTab } from "@/notes/NotesTab";
import { VehicleContractSection } from "@/vehicleContracts/VehicleContractSection";
import { VehicleContractsTab } from "@/vehicleContracts/VehicleContractsTab";
import { VehicleContractAuditPanel } from "@/vehicleContracts/VehicleContractAuditPanel";
import {
  VEHICLE_CONTRACT_AUDIT_PERMISSIONS,
  VEHICLE_CONTRACT_READ_PERMISSION,
} from "@/vehicleContracts/vehicleContractPermissions";
import { OpenVehicleHandoverCard } from "@/vehicleHandovers/OpenVehicleHandoverCard";
import { ReportVehicleHandoverButton } from "@/vehicleHandovers/ReportVehicleHandoverButton";
import { toVehicleSpecificationDetailFields } from "@/vehicleSpecifications/vehicleSpecification";
import type { Vehicle } from "@/vehicles/vehicle";
import {
  formatKilometers,
  getVehicleLicensePlate,
  getVehicleTitle,
} from "@/vehicles/vehicleFields";
import { VehicleArchiveDialog } from "@/vehicles/VehicleArchiveDialog";
import { VehicleEditSheet } from "@/vehicles/VehicleEditSheet";
import { useUpdateVehicle } from "@/vehicles/useUpdateVehicle";
import {
  useArchiveVehicle,
  useUnarchiveVehicle,
} from "@/vehicles/useArchiveVehicle";

const FINE_READ_PERMISSION = "admin_fine_read";
const CHARGING_CARD_READ_PERMISSION = "admin_charging_card_assignment_read";

const DETAIL_TABS = [
  "gegevens",
  "contracten",
  "wijzigingslog",
  "bekeuringen",
  "notities",
  "documenten",
] as const;
type DetailTab = (typeof DETAIL_TABS)[number];
const DEFAULT_TAB: DetailTab = "gegevens";

/** The permission(s) a tab needs, for the tabs that are gated. */
const TAB_PERMISSIONS: Partial<Record<DetailTab, string | readonly string[]>> =
  {
    contracten: VEHICLE_CONTRACT_READ_PERMISSION,
    wijzigingslog: VEHICLE_CONTRACT_AUDIT_PERMISSIONS,
    bekeuringen: FINE_READ_PERMISSION,
    notities: NOTE_READ_PERMISSION,
    documenten: DOCUMENT_READ_PERMISSION,
  };

function isDetailTab(value: unknown): value is DetailTab {
  return DETAIL_TABS.includes(value as DetailTab);
}

interface VehicleDetailSearch {
  readonly tab: DetailTab;
  /**
   * The contract to open the renewal form for, straight away.
   *
   * A renewal is decided somewhere else - on the expiration of the contract -
   * and carried out here. Naming the contract in the URL lets that other screen
   * hand the work over whole, instead of dropping the user on a list of
   * contracts with the one they meant still to be found.
   */
  readonly renew?: string;
}

export const Route = createFileRoute("/_authenticated/vehicles_/$vehicleId")({
  // The active tab lives in the URL so a deep link/reload lands back on the
  // same tab, mirroring the dossier detail route.
  validateSearch: (search: Record<string, unknown>): VehicleDetailSearch => ({
    tab: isDetailTab(search.tab) ? search.tab : DEFAULT_TAB,
    renew: typeof search.renew === "string" ? search.renew : undefined,
  }),
  component: VehicleDetailPage,
});

function VehicleDetailPage(): ReactNode {
  const { vehicleId } = Route.useParams();
  const { tab, renew } = Route.useSearch();
  const navigate = Route.useNavigate();
  const { data, isPending, isError, refetch } =
    useGetAdminVehiclesVehicleId(vehicleId);

  return (
    <div className="space-y-4">
      <Link
        to="/vehicles"
        search={{ page: 1 }}
        className="inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="size-4" aria-hidden="true" />
        Terug naar voertuigen
      </Link>

      {isError ? (
        <Card>
          <CardContent className="py-10 text-center">
            <div className="space-y-3">
              <p className="text-muted-foreground">
                We konden dit voertuig niet laden.
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => void refetch()}
              >
                Opnieuw proberen
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* The header stays above the tabs so the title and the archive
              action remain reachable from every tab. */}
          <VehicleDetailHeader
            vehicleId={vehicleId}
            vehicle={data}
            isPending={isPending}
          />
          <VehicleDetailTabs
            vehicleId={vehicleId}
            vehicle={data}
            isPending={isPending}
            tab={tab}
            renew={renew}
            onRenewOpened={() => {
              // Spent the moment the form is up: a reload or a second visit to
              // the tab should show the contracts, not the form again.
              void navigate({
                search: (prev) => ({ ...prev, renew: undefined }),
                replace: true,
              });
            }}
            onTabChange={(nextTab) => {
              void navigate({
                search: (prev) => ({ ...prev, tab: nextTab }),
              });
            }}
          />
        </>
      )}
    </div>
  );
}

function VehicleDetailTabs({
  vehicleId,
  vehicle,
  isPending,
  tab,
  renew,
  onRenewOpened,
  onTabChange,
}: {
  readonly vehicleId: string;
  readonly vehicle: Vehicle | undefined;
  readonly isPending: boolean;
  readonly tab: DetailTab;
  readonly renew: string | undefined;
  readonly onRenewOpened: () => void;
  readonly onTabChange: (tab: DetailTab) => void;
}): ReactNode {
  const user = useCurrentUser();

  // `tab` comes from the URL, but most tabs are permission-gated. A deep link to
  // a gated tab from a user without its permission would leave the Tabs root on
  // a value that has no mounted trigger or panel, which blanks the whole tab
  // strip rather than just that one panel. Resolve the effective tab here so the
  // URL can never select something that is not rendered.
  const requiredPermission = TAB_PERMISSIONS[tab];
  const activeTab =
    requiredPermission && !hasPermission(user, requiredPermission)
      ? DEFAULT_TAB
      : tab;

  return (
    <Tabs
      value={activeTab}
      onValueChange={(value) => {
        if (isDetailTab(value)) {
          onTabChange(value);
        }
      }}
      className="flex flex-col gap-5"
    >
      <TabsList className="self-start">
        <TabsTrigger value="gegevens">Gegevens</TabsTrigger>
        <RequirePermission permission={VEHICLE_CONTRACT_READ_PERMISSION}>
          <TabsTrigger value="contracten">Contracten</TabsTrigger>
        </RequirePermission>
        <RequirePermission permission={VEHICLE_CONTRACT_AUDIT_PERMISSIONS}>
          <TabsTrigger value="wijzigingslog">Wijzigingslog</TabsTrigger>
        </RequirePermission>
        <RequirePermission permission={FINE_READ_PERMISSION}>
          <TabsTrigger value="bekeuringen">Bekeuringen</TabsTrigger>
        </RequirePermission>
        <RequirePermission permission={NOTE_READ_PERMISSION}>
          <TabsTrigger value="notities">Notities</TabsTrigger>
        </RequirePermission>
        <RequirePermission permission={DOCUMENT_READ_PERMISSION}>
          <TabsTrigger value="documenten">Documenten</TabsTrigger>
        </RequirePermission>
      </TabsList>

      <TabsContent value="gegevens" className="flex flex-col gap-5">
        {/* Above the vehicle's own facts: a hand-in that is running is what
            the page is about until it is settled. */}
        {vehicle?.openVehicleHandover ? (
          <OpenVehicleHandoverCard
            vehicleHandoverId={vehicle.openVehicleHandover}
            status={vehicle.openVehicleHandoverStatus}
          />
        ) : null}
        <VehicleTab vehicle={vehicle} isPending={isPending} />
        <RequirePermission permission={VEHICLE_CONTRACT_READ_PERMISSION}>
          <VehicleContractSection vehicleId={vehicleId} />
        </RequirePermission>
        <RequirePermission permission={CHARGING_CARD_READ_PERMISSION}>
          <MobilityCardsSection subject={{ kind: "vehicle", id: vehicleId }} />
        </RequirePermission>
      </TabsContent>

      <RequirePermission permission={VEHICLE_CONTRACT_READ_PERMISSION}>
        <TabsContent value="contracten">
          <VehicleContractsTab
            vehicleId={vehicleId}
            renewContractId={renew}
            onRenewOpened={onRenewOpened}
          />
        </TabsContent>
      </RequirePermission>

      <RequirePermission permission={VEHICLE_CONTRACT_AUDIT_PERMISSIONS}>
        <TabsContent value="wijzigingslog">
          <VehicleContractAuditPanel vehicleId={vehicleId} />
        </TabsContent>
      </RequirePermission>

      <RequirePermission permission={FINE_READ_PERMISSION}>
        <TabsContent value="bekeuringen">
          <FinesTab subject={{ type: "vehicle", id: vehicleId }} />
        </TabsContent>
      </RequirePermission>

      <RequirePermission permission={NOTE_READ_PERMISSION}>
        <TabsContent value="notities">
          <NotesTab subject={{ type: "vehicle", id: vehicleId }} />
        </TabsContent>
      </RequirePermission>

      <RequirePermission permission={DOCUMENT_READ_PERMISSION}>
        <TabsContent value="documenten">
          <DocumentsTab subject={{ type: "vehicle", id: vehicleId }} />
        </TabsContent>
      </RequirePermission>
    </Tabs>
  );
}

function VehicleDetailHeader({
  vehicleId,
  vehicle,
  isPending,
}: {
  readonly vehicleId: string;
  readonly vehicle: Vehicle | undefined;
  readonly isPending: boolean;
}): ReactNode {
  if (isPending || !vehicle) {
    return (
      <div className="flex items-center gap-3">
        <Skeleton className="size-12 rounded-md" />
        <div className="space-y-2">
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-32" />
        </div>
      </div>
    );
  }

  const plate = getVehicleLicensePlate(vehicle);

  return (
    <div className="flex flex-wrap items-center gap-3">
      <VehicleImage
        brand={vehicle.brand ?? undefined}
        model={vehicle.model ?? undefined}
        color={vehicle.color ?? undefined}
        size="lg"
      />
      <div className="min-w-0">
        <h1 className="truncate text-2xl font-semibold tracking-tight">
          {getVehicleTitle(vehicle)}
        </h1>
        <div className="mt-1 flex items-center gap-2">
          {plate ? (
            <LicensePlate value={plate} size="sm" />
          ) : (
            <span className="text-sm text-muted-foreground">Kentekenloos</span>
          )}
          {vehicle.archived ? (
            <Badge variant="neutral" size="sm">
              Gearchiveerd
            </Badge>
          ) : (
            <Badge variant="success" size="sm">
              Actief
            </Badge>
          )}
        </div>
      </div>
      <div className="ml-auto flex flex-wrap items-center gap-2">
        <VehicleEditAction vehicleId={vehicleId} vehicle={vehicle} />
        {vehicle.openVehicleHandover || vehicle.archived === true ? null : (
          <ReportVehicleHandoverButton vehicleId={vehicleId} />
        )}
        <VehicleArchiveActions
          vehicleId={vehicleId}
          archived={vehicle.archived === true}
        />
      </div>
    </div>
  );
}

function VehicleEditAction({
  vehicleId,
  vehicle,
}: {
  readonly vehicleId: string;
  readonly vehicle: Vehicle;
}): ReactNode {
  const toast = useToast();
  const [editOpen, setEditOpen] = useState(false);
  const updateMutation = useUpdateVehicle(vehicleId);

  return (
    <RequirePermission permission="admin_vehicle_update">
      <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
        <Pencil className="size-4" aria-hidden="true" />
        Bewerken
      </Button>
      <VehicleEditSheet
        open={editOpen}
        onOpenChange={setEditOpen}
        vehicle={vehicle}
        isSubmitting={updateMutation.isPending}
        onSubmit={(input) =>
          updateMutation.mutate(input, {
            onSuccess: () => {
              setEditOpen(false);
              toast.success("Voertuig bijgewerkt.");
            },
          })
        }
      />
    </RequirePermission>
  );
}

function VehicleArchiveActions({
  vehicleId,
  archived,
}: {
  readonly vehicleId: string;
  readonly archived: boolean;
}): ReactNode {
  const toast = useToast();
  const [archiveOpen, setArchiveOpen] = useState(false);
  const archiveMutation = useArchiveVehicle(vehicleId);
  const unarchiveMutation = useUnarchiveVehicle(vehicleId);

  if (archived) {
    return (
      <RequirePermission permission="admin_vehicle_archive">
        <Button
          variant="outline"
          size="sm"
          disabled={unarchiveMutation.isPending}
          onClick={() =>
            unarchiveMutation.mutate({
              onSuccess: () => toast.success("Voertuig gedearchiveerd."),
            })
          }
        >
          {unarchiveMutation.isPending ? (
            <Loader2 className="size-4 animate-spin" aria-hidden="true" />
          ) : (
            <ArchiveRestore className="size-4" aria-hidden="true" />
          )}
          Dearchiveren
        </Button>
      </RequirePermission>
    );
  }

  return (
    <RequirePermission permission="admin_vehicle_archive">
      <Button variant="outline" size="sm" onClick={() => setArchiveOpen(true)}>
        <Archive className="size-4" aria-hidden="true" />
        Archiveren
      </Button>
      <VehicleArchiveDialog
        open={archiveOpen}
        onOpenChange={setArchiveOpen}
        isSubmitting={archiveMutation.isPending}
        onSubmit={(input) =>
          archiveMutation.mutate(input, {
            onSuccess: () => {
              setArchiveOpen(false);
              toast.success("Voertuig gearchiveerd.");
            },
          })
        }
      />
    </RequirePermission>
  );
}

function VehicleTab({
  vehicle,
  isPending,
}: {
  readonly vehicle: Vehicle | undefined;
  readonly isPending: boolean;
}): ReactNode {
  if (isPending || !vehicle) {
    return (
      <Card>
        <CardContent className="space-y-3 py-6">
          {["a", "b", "c", "d"].map((key) => (
            <Skeleton key={key} className="h-4 w-full max-w-md" />
          ))}
        </CardContent>
      </Card>
    );
  }

  // `FieldGrid` drops the fields the API left empty, so the cards stay free of
  // rows of em dashes; the two sections split the static vehicle facts from the
  // things that change while the vehicle is in service.
  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <DetailSection title="Voertuiggegevens" icon={Car}>
        <FieldGrid
          fields={[
            { label: "Merk", value: vehicle.brand ?? undefined },
            { label: "Model", value: vehicle.model ?? undefined },
            { label: "VIN", value: vehicle.vinNumber ?? undefined },
            { label: "Brandstof", value: vehicle.fuelType ?? undefined },
            { label: "Kleur", value: vehicle.color ?? undefined },
            {
              label: "Bouwjaar",
              value: vehicle.buildYear ? String(vehicle.buildYear) : undefined,
            },
          ]}
        />
      </DetailSection>

      <DetailSection title="Beheer" icon={UserRound}>
        <FieldGrid
          fields={[
            { label: "Locatie", value: vehicle.siteName ?? undefined },
            {
              label: "Leasemaatschappij",
              value: vehicle.leaseCompanyName ?? undefined,
            },
            { label: "Dealer", value: vehicle.dealershipName ?? undefined },
            {
              label: "Km-stand",
              value: formatKilometers(vehicle.latestOdometerKilometers),
            },
          ]}
        />
      </DetailSection>

      <DetailSection title="Specificaties" icon={Ruler}>
        <FieldGrid
          fields={toVehicleSpecificationDetailFields(vehicle.specification).map(
            (field) => ({
              label: field.label,
              value: field.value ?? undefined,
            }),
          )}
        />
      </DetailSection>
    </div>
  );
}
